import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { http } from "../api/http";
import type { Location, Warehouse } from "../types";
import { useAuth } from "./AuthContext";

interface WorkspaceContextValue {
    warehouses: Warehouse[];
    warehouse?: Warehouse;
    warehouseId?: number;
    locations: Location[];
    setWarehouseId: (id: number) => void;
    reloadWarehouses: () => Promise<void>;
    reloadLocations: () => Promise<void>;
    loading: boolean;
}
const WorkspaceContext = createContext<WorkspaceContextValue | null>(null);

export function WorkspaceProvider({ children }: { children: ReactNode }) {
    const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
    const [locations, setLocations] = useState<Location[]>([]);
    const [loading, setLoading] = useState(true);
    const [warehouseId, setWarehouseIdState] = useState<number | undefined>(() => {
        const stored = Number(localStorage.getItem("wms.warehouseId"));
        return stored > 0 ? stored : undefined;
    });
    const reloadWarehouses = useCallback(async () => {
        const { data } = await http.get<Warehouse[]>("/warehouses");
        const active = data.filter((item) => item.active);
        setWarehouses(active);
        setWarehouseIdState((current) =>
            current && active.some((warehouse) => warehouse.id === current) ? current : active[0]?.id,
        );
    }, []);
    useEffect(() => {
        void reloadWarehouses().finally(() => setLoading(false));
    }, [reloadWarehouses]);
    const reloadLocations = useCallback(async () => {
        if (!warehouseId) {
            setLocations([]);
            return;
        }
        const { data } = await http.get<Location[]>(`/warehouses/${warehouseId}/locations`);
        setLocations(data);
    }, [warehouseId]);
    useEffect(() => {
        void reloadLocations();
    }, [reloadLocations]);
    // Recarrega as permissões efetivas no escopo do armazém ativo.
    const { refreshPermissions } = useAuth();
    useEffect(() => {
        void refreshPermissions(warehouseId);
    }, [warehouseId, refreshPermissions]);
    const setWarehouseId = (id: number) => {
        localStorage.setItem("wms.warehouseId", String(id));
        setWarehouseIdState(id);
    };
    const value = useMemo(
        () => ({
            warehouses,
            warehouseId,
            locations,
            warehouse: warehouses.find((item) => item.id === warehouseId),
            setWarehouseId,
            reloadWarehouses,
            reloadLocations,
            loading,
        }),
        [loading, locations, reloadLocations, reloadWarehouses, warehouseId, warehouses],
    );
    return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}

export function useWorkspace() {
    const value = useContext(WorkspaceContext);
    if (!value) throw new Error("useWorkspace deve ser usado dentro de WorkspaceProvider");
    return value;
}
